import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Helmet } from 'react-helmet';
import { ChevronRight, Clock, ArrowLeft, MessageCircle, Flame, Dumbbell, Users, ChefHat, Lightbulb } from 'lucide-react';
import { recipesData } from '@/data/recipesData';

const RecipeDetailPage = () => {
  const { slug } = useParams();
  const recipe = recipesData.find((r) => r.slug === slug);

  const relatedRecipes = useMemo(() => {
    if (!recipe) return [];
    return recipesData.filter((r) => r.id !== recipe.id).slice(0, 3);
  }, [recipe]);

  if (!recipe) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center pt-32">
          <h1 className="text-3xl font-bold text-foreground mb-4">Recipe Not Found</h1>
          <p className="text-muted-foreground mb-6">The recipe you&apos;re looking for doesn&apos;t exist.</p>
          <Button asChild>
            <Link to="/recipes">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Recipes
            </Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Recipe',
    name: recipe.title,
    description: recipe.excerpt,
    image: recipe.image,
    author: { '@type': 'Organization', name: 'NutraFuel Supplements' },
    prepTime: `PT${parseInt(recipe.prepTime)}M`,
    recipeYield: `${recipe.servings} serving${recipe.servings > 1 ? 's' : ''}`,
    recipeCategory: recipe.category,
    keywords: recipe.tags.join(', '),
    nutrition: {
      '@type': 'NutritionInformation',
      calories: `${recipe.calories} calories`,
      proteinContent: recipe.protein,
      carbohydrateContent: recipe.carbs,
      fatContent: recipe.fat,
    },
    recipeIngredient: recipe.ingredients,
    recipeInstructions: recipe.instructions.map((step, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      text: step,
    })),
  };

  return (
    <>
      <Helmet>
        <title>{`${recipe.title} - High Protein Recipe | NutraFuel`}</title>
        <meta name="description" content={recipe.excerpt} />
        <meta name="keywords" content={recipe.tags.join(', ')} />
        <link rel="canonical" href={`https://nutrafuel.in/recipes/${recipe.slug}`} />
        <meta property="og:title" content={recipe.title} />
        <meta property="og:description" content={recipe.excerpt} />
        <meta property="og:image" content={recipe.image} />
        <meta property="og:url" content={`https://nutrafuel.in/recipes/${recipe.slug}`} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Layout>
        {/* Breadcrumb */}
        <div className="bg-muted/30 pt-24 pb-3 border-b border-border">
          <div className="container mx-auto px-4">
            <nav className="flex items-center text-sm text-muted-foreground" data-testid="recipe-breadcrumb">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5 mx-2" />
              <Link to="/recipes" className="hover:text-primary transition-colors">Recipes</Link>
              <ChevronRight className="w-3.5 h-3.5 mx-2" />
              <span className="text-foreground font-medium truncate max-w-[250px]">{recipe.title}</span>
            </nav>
          </div>
        </div>

        {/* Recipe Header */}
        <section className="py-10 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Badge className="mb-4 bg-primary/10 text-primary border-0">{recipe.category}</Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground leading-tight mb-6" data-testid="recipe-detail-title">
                {recipe.title}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">{recipe.excerpt}</p>

              {/* Quick Info Bar */}
              <div className="flex flex-wrap gap-4" data-testid="recipe-quick-info">
                <span className="inline-flex items-center gap-1.5 text-sm font-medium bg-muted px-3 py-1.5 rounded-full">
                  <Clock className="w-4 h-4 text-primary" /> {recipe.prepTime}
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium bg-muted px-3 py-1.5 rounded-full">
                  <Users className="w-4 h-4 text-primary" /> {recipe.servings} serving{recipe.servings > 1 ? 's' : ''}
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium bg-muted px-3 py-1.5 rounded-full">
                  <ChefHat className="w-4 h-4 text-primary" /> {recipe.difficulty}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <div className="container mx-auto px-4 mb-10">
          <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden">
            <img
              src={recipe.image}
              alt={recipe.title}
              className="w-full h-64 sm:h-80 md:h-96 object-cover"
            />
          </div>
        </div>

        {/* Macros Card */}
        <div className="container mx-auto px-4 mb-10">
          <div className="max-w-3xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="recipe-macros">
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
                <Flame className="w-5 h-5 text-orange-500 mx-auto mb-1" />
                <p className="text-2xl font-bold text-orange-600">{recipe.calories}</p>
                <p className="text-xs text-orange-500 font-medium">Calories</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <Dumbbell className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                <p className="text-2xl font-bold text-blue-600">{recipe.protein}</p>
                <p className="text-xs text-blue-500 font-medium">Protein</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <svg className="w-5 h-5 text-green-500 mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                <p className="text-2xl font-bold text-green-600">{recipe.carbs}</p>
                <p className="text-xs text-green-500 font-medium">Carbs</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                <svg className="w-5 h-5 text-purple-500 mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                <p className="text-2xl font-bold text-purple-600">{recipe.fat}</p>
                <p className="text-xs text-purple-500 font-medium">Fat</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recipe Content */}
        <section className="pb-16 bg-background" data-testid="recipe-detail-content">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">

              {/* Description */}
              <p className="text-base text-muted-foreground leading-relaxed mb-10">
                {recipe.description}
              </p>

              {/* Ingredients */}
              <div className="mb-10" data-testid="recipe-ingredients">
                <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-6 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">1</span>
                  Ingredients
                </h2>
                <div className="bg-muted/30 rounded-xl border border-border p-6">
                  <ul className="space-y-3">
                    {recipe.ingredients.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-base text-muted-foreground leading-relaxed">
                        <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Instructions */}
              <div className="mb-10" data-testid="recipe-instructions">
                <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-6 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">2</span>
                  Instructions
                </h2>
                <ol className="space-y-4">
                  {recipe.instructions.map((step, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-base text-muted-foreground leading-relaxed pt-0.5">{step}</p>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Tips */}
              {recipe.tips && recipe.tips.length > 0 && (
                <div className="mb-10" data-testid="recipe-tips">
                  <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-6 flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-gold text-sm">
                      <Lightbulb className="w-4 h-4" />
                    </span>
                    Pro Tips
                  </h2>
                  <div className="bg-gold/5 rounded-xl border border-gold/20 p-6">
                    <ul className="space-y-3">
                      {recipe.tips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-3 text-base text-muted-foreground leading-relaxed">
                          <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0 mt-2.5" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="mt-14 p-8 rounded-2xl bg-gradient-to-r from-primary/10 via-gold/10 to-primary/10 border border-primary/20 text-center">
                <h3 className="text-xl font-bold text-foreground mb-3">
                  Get the Best Protein for Your Recipes
                </h3>
                <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                  Shop 100% authentic whey protein and supplements at NutraFuel to make your recipes even better.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <Button asChild>
                    <Link to="/products" data-testid="recipe-cta-products">Shop Supplements</Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <a
                      href="https://wa.me/9779824042067?text=Hi!%20I%20saw%20your%20recipes%20and%20want%20to%20order%20supplements."
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="recipe-cta-whatsapp"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Order on WhatsApp
                    </a>
                  </Button>
                </div>
              </div>

              {/* Tags */}
              <div className="mt-10 flex flex-wrap gap-2">
                {recipe.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    #{tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Recipes */}
        {relatedRecipes.length > 0 && (
          <section className="py-14 bg-muted/20 border-t border-border" data-testid="related-recipes-section">
            <div className="container mx-auto px-4">
              <h2 className="text-2xl font-display font-bold text-foreground mb-8">More Recipes</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedRecipes.map((rr) => (
                  <Link key={rr.id} to={`/recipes/${rr.slug}`} data-testid={`related-recipe-${rr.id}`}>
                    <Card className="group overflow-hidden border-0 shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-1 h-full">
                      <div className="aspect-video overflow-hidden bg-muted">
                        <img
                          src={rr.image}
                          alt={rr.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="secondary" className="text-xs">{rr.category}</Badge>
                          <span className="text-xs text-muted-foreground">{rr.protein} protein</span>
                        </div>
                        <h3 className="font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                          {rr.title}
                        </h3>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </Layout>
    </>
  );
};

export default RecipeDetailPage;
